import { create } from 'zustand';
import { utils } from '@ohif/core';

const { formatDate } = utils;

export type ImportedStudy = {
  studyInstanceUID: string;
  studyDescription: string;
  studyDate: string;
  modalities: string;
  numInstances: number;
  importedAt: number;
};

export type ImportedStudyBrowserEntry = {
  studyInstanceUid: string;
  date: string;
  rawStudyDate: string;
  studyTime: string;
  description: string;
  modalities: string;
  numInstances: number;
  isImported: true;
};

type ImportedStudyInput = Omit<ImportedStudy, 'importedAt'>;

type ImportedStudyState = {
  studies: ImportedStudy[];
  activeStudyInstanceUID: string | null;
  addStudies: (records: ImportedStudyInput[]) => string[];
  setActiveStudy: (studyInstanceUID: string | null) => void;
  clearImportedStudies: () => void;
  isImportedStudyUID: (studyInstanceUID: string) => boolean;
  getBrowserEntries: () => ImportedStudyBrowserEntry[];
};

function toBrowserEntry(study: ImportedStudy): ImportedStudyBrowserEntry {
  return {
    studyInstanceUid: study.studyInstanceUID,
    date: formatDate(study.studyDate) || '',
    rawStudyDate: study.studyDate || '',
    studyTime: '',
    description: study.studyDescription || 'Imported Study',
    modalities: study.modalities || '',
    numInstances: study.numInstances,
    isImported: true,
  };
}

export const useImportedStudyStore = create<ImportedStudyState>((set, get) => ({
  studies: [],
  activeStudyInstanceUID: null,

  addStudies: records => {
    const touchedStudyUIDs: string[] = [];
    const now = Date.now();

    set(state => {
      const studies = [...state.studies];

      records.forEach(record => {
        const existingIndex = studies.findIndex(
          study => study.studyInstanceUID === record.studyInstanceUID
        );

        if (existingIndex >= 0) {
          studies[existingIndex] = {
            ...studies[existingIndex],
            ...record,
            importedAt: studies[existingIndex].importedAt,
          };
        } else {
          studies.push({
            ...record,
            importedAt: now,
          });
        }

        touchedStudyUIDs.push(record.studyInstanceUID);
      });

      studies.sort((a, b) => b.importedAt - a.importedAt);

      const activeStudyInstanceUID =
        touchedStudyUIDs.length === 1
          ? touchedStudyUIDs[0]
          : state.activeStudyInstanceUID && touchedStudyUIDs.includes(state.activeStudyInstanceUID)
            ? state.activeStudyInstanceUID
            : touchedStudyUIDs[0] ?? state.activeStudyInstanceUID;

      return {
        studies,
        activeStudyInstanceUID,
      };
    });

    return touchedStudyUIDs;
  },

  setActiveStudy: studyInstanceUID => {
    set(state => {
      if (
        studyInstanceUID &&
        !state.studies.some(study => study.studyInstanceUID === studyInstanceUID)
      ) {
        return state;
      }

      return { activeStudyInstanceUID: studyInstanceUID };
    });
  },

  clearImportedStudies: () => {
    set({
      studies: [],
      activeStudyInstanceUID: null,
    });
  },

  isImportedStudyUID: studyInstanceUID =>
    get().studies.some(study => study.studyInstanceUID === studyInstanceUID),

  getBrowserEntries: () => get().studies.map(toBrowserEntry),
}));
